'use client';

import { Loader2, Pause, Volume2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useTranslation } from '@/lib/i18n';
import { useTextToSpeech } from '@/hooks/use-text-to-speech';

export interface TextToSpeechButtonProps {
  /** 内容 id（消息 id），用于全局朗读互斥 */
  id: string;
  /** 待朗读的音频来源 */
  audioSources: string[] | string; // 存储音频来源的文本内容
  /** 业务音色 ID（见 lib/providers/tts/voices.ts）；不传则跟随当前界面语言 */
  voice?: string;
  /** 用量上报归因，透传给 /api/tts */
  chatId?: string;
  messageId?: string;
  /** 生成中等场景下禁用 */
  disabled?: boolean;
  size?: number;
  className?: string;
}

/**
 * 朗读按钮：纯视图。只负责图标状态与无障碍属性，
 * 请求、分句、播放队列全部在 useTextToSpeech 里。
 */
export default function TextToSpeechButton({
  id,
  audioSources,
  voice,
  chatId,
  messageId,
  disabled = false,
  size = 18,
  className,
}: TextToSpeechButtonProps) {
  const { t } = useTranslation();
  const { isLoading, isSpeaking, isPaused, canSpeak, errorKind, toggle } = useTextToSpeech({
    id,
    audioSources,
    voice,
    chatId,
    // 没显式传就用消息 id 本身，计费归因至少能落到具体这条回答上
    messageId: messageId ?? id,
  });

  const isBusy = isLoading || isSpeaking;
  const isDisabled = disabled || !canSpeak;

  const errorLabel =
    errorKind === 'insufficient-credits'
      ? t('Not enough credits to read aloud')
      : errorKind === 'unavailable'
        ? t('Read aloud is temporarily unavailable')
        : errorKind
          ? t('Read aloud failed')
          : null;

  const label =
    errorLabel ??
    (isLoading
      ? t('Preparing audio...')
      : isSpeaking
        ? t('Pause reading')
        : isPaused
          ? t('Resume reading')
          : t('Read aloud'));

  return (
    <button
      type="button"
      onClick={toggle}
      disabled={isDisabled}
      title={label}
      aria-label={label}
      aria-pressed={isBusy}
      aria-busy={isLoading}
      className={cn(
        'text-black/70 dark:text-text-tertiary size-8 rounded-full flex items-center justify-center transition duration-200',
        'hover:bg-[#E9E9E9] dark:hover:bg-[#1e1e1e] hover:text-black dark:hover:text-white',
        isDisabled && 'opacity-30 cursor-not-allowed',
        isBusy && 'text-black dark:text-white',
        className,
      )}
    >
      {isLoading ? (
        <Loader2 size={size} className="animate-spin" />
      ) : isSpeaking ? (
        <Pause size={size} />
      ) : (
        <Volume2 size={size} />
      )}
    </button>
  );
}
